import React from 'react';
import PropTypes from 'prop-types';
import {
	AlertIcon,
	StatusIcon,
	SearchIcon,
	HealthCheckIcon,
	BatteryIcon,
	PowerIcon,
	NavigationIcon,
} from 'components/Icons';

import moment from 'moment';
import i18next from 'i18next';
import s from './style.module.scss';
import Spinner from '../../UI/Spinner';
import ModalPopUp from '../../UI/ModalPopUp';

class DataList extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			showMap: false,
		};
		this.mapNode = null;
		this.mapInstance = null;
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.state.showMap && !prevState.showMap) {
			// Initialize map after modal becomes visible
			this.initMap();
		}
	}

	openMap = () => {
		this.setState({ showMap: true });
	};

	closeMap = () => {
		this.setState({ showMap: false });
	};

	initMap = () => {
		try {
			const loc = this.props?.dataList?.[0]?.last_location;
			if (!this.mapNode || !loc) return;
			if (!(window && window.google && window.google.maps)) return;

			const hasLatLng =
				typeof loc?.latitude === 'number' && typeof loc?.longitude === 'number';

			if (hasLatLng) {
				const center = { lat: loc.latitude, lng: loc.longitude };
				const map = new window.google.maps.Map(this.mapNode, {
					center,
					zoom: 12,
					disableDefaultUI: true,
					zoomControl: true,
					minZoom: 3,
				});
				new window.google.maps.Marker({
					position: center,
					map,
					title: loc?.last_location || '',
				});
				this.mapInstance = map;
				return;
			}

			// No lat/long: do not load the map; handled by UI message
			return;
		} catch (e) {
			// Log error for debugging while preventing UI break; map is optional enhancement
			console.error('Map initialization failed:', e);
		}
	};

	render() {
		const { dataList, title, notificationButton, isAllowUpdate } = this.props;

		const marginTopStyle =
			title === i18next.t('device_health') ? { marginTop: '16px' } : {};

		const dataListStyle = {
			border: '1px solid #E0E0E0',
			borderRadius: '8px',
			...marginTopStyle,
		};

		let titleIcon;

		if (title === i18next.t('device_specification')) {
			titleIcon = <SearchIcon className={s.labelSpacing} />;
		} else if (title === i18next.t('device_health')) {
			titleIcon = <HealthCheckIcon className={s.labelSpacing} />;
		} else {
			titleIcon = null;
		}

		let fieldsToRender;

		if (title === i18next.t('device_specification')) {
			let lastConnect;
			const utcDateTime = new Date(dataList[0]?.last_connected);
			const localDateTime = new Date(
				utcDateTime.getTime() - utcDateTime.getTimezoneOffset() * 60000,
			);
			if (dataList) {
				if (moment(localDateTime).isBefore(moment().subtract(3, 'd'))) {
					lastConnect = `${moment(localDateTime).format('DD MMM YYYY')}`;
				} else {
					lastConnect = moment(localDateTime).fromNow();
				}
			}

			fieldsToRender = (
				<>
					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>{i18next.t('device_model')}</span>
							<div className={s.labelValues}>
								{dataList ? dataList[0]?.device_model : 'N/A'}
							</div>
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>{i18next.t('serial_number')}</span>
							<div className={s.labelValues}>
								{dataList && dataList[0]?.serial_number !== null
									? dataList[0].serial_number
									: 'N/A'}
							</div>
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>
								{i18next.t('firmware_version')}
							</span>
							<div className={s.labelValues}>
								{dataList && dataList?.[0]?.firmware_version !== null
									? dataList[0].firmware_version
									: 'N/A'}
							</div>
							<div className={s.firmwareRow}>
								<span className={s.fieldIcon}>{notificationButton}</span>
								{isAllowUpdate ? (
									<span className={s.fieldIcon}>
										<AlertIcon />
									</span>
								) : null}
							</div>
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>
								{i18next.t('last_connected')}
							</span>
							<div className={s.labelValues}>{lastConnect}</div>
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>{i18next.t('last_location')}</span>
							<div className={s.labelValues}>
								{dataList && dataList?.[0]?.last_location ? (
									<>
										<span>
											{dataList[0].last_location?.last_location || 'N/A'}
										</span>
										<span
											className={s.fieldIcon}
											style={{ cursor: 'pointer', marginLeft: '8px' }}
											onClick={this.openMap}
											title={i18next.t('view_on_map')}
										>
											<NavigationIcon />
										</span>
									</>
								) : (
									'N/A'
								)}
							</div>
						</div>
					</div>
				</>
			);
		} else if (title === i18next.t('device_health')) {
			var powerValue = [];
			var batteryCapacityValue = [];
			var sieveLifeValue = [];
			var oxygenPurityValue = [];
			var lifeClockValue = [];
			if (typeof dataList === 'object' && dataList.length) {
				dataList.forEach((item) => {
					if (item.name === 'power') {
						powerValue = item;
					}
					if (item.name === 'battery_capacity') {
						batteryCapacityValue = item;
					}
					if (item.name === 'sieve_life') {
						sieveLifeValue = item;
					}
					if (item.name === 'oxygen_purity') {
						oxygenPurityValue = item;
					}
					if (item.name === 'life_clock') {
						lifeClockValue = item;
					}
				});
			}

			const minutes = lifeClockValue?.value || 'N/A';
			const hours = Math.floor(minutes / 60);
			const remainingMinutes = minutes % 60;
			const result = `${hours ? hours : '0'} ${i18next.t('hrs')} ${
				remainingMinutes ? remainingMinutes : '0'
			} ${i18next.t('min')}`;

			fieldsToRender = (
				<>
					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>{i18next.t('power')}</span>
							{this.props.loadingHealthCheckData === true ? (
								<div className={s.labelValues}>
									<Spinner small />
								</div>
							) : powerValue?.value !== null &&
							  powerValue?.value !== undefined ? (
								<>
									<div className={s.labelValues}>
										{powerValue?.value === '0' && powerValue?.value !== null
											? i18next.t('battery')
											: powerValue?.value === '1'
											  ? i18next.t('voltage')
											  : '-'}
									</div>
									<span className={s.fieldIcon}>
										{powerValue?.value === '0' && powerValue?.value !== null ? (
											<BatteryIcon />
										) : powerValue?.value === '1' ? (
											<PowerIcon />
										) : (
											''
										)}
									</span>
								</>
							) : (
								<div className={s.labelValues}>-</div>
							)}
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>
								{i18next.t('battery_health')}
							</span>
							{this.props.loadingHealthCheckData === true ? (
								<div className={s.labelValues}>
									<Spinner small />
								</div>
							) : batteryCapacityValue.value !== null &&
							  batteryCapacityValue.value !== undefined ? (
								<>
									<div className={s.labelValues}>
										{batteryCapacityValue?.is_failed === true
											? i18next.t('bad')
											: batteryCapacityValue?.is_actual === true
											  ? i18next.t('good')
											  : '-'}

										{batteryCapacityValue.value !== null &&
										batteryCapacityValue.value !== '0' ? (
											<span className={s.labelValues}>
												{` (${batteryCapacityValue.value}%)`}
											</span>
										) : (
											''
										)}
									</div>
									<span className={s.fieldIcon}>
										{batteryCapacityValue?.is_failed === true ? (
											<AlertIcon />
										) : batteryCapacityValue?.is_actual === true ? (
											<StatusIcon />
										) : (
											''
										)}
									</span>
								</>
							) : (
								<div className={s.labelValues}>-</div>
							)}
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>{i18next.t('column_life')}</span>
							{this.props.loadingHealthCheckData === true ? (
								<div className={s.labelValues}>
									<Spinner small />
								</div>
							) : sieveLifeValue.value !== null &&
							  sieveLifeValue.value !== undefined ? (
								<>
									<div className={s.labelValues}>
										{sieveLifeValue?.is_failed === true
											? i18next.t('bad')
											: sieveLifeValue?.is_actual === true
											  ? i18next.t('good')
											  : '-'}

										{sieveLifeValue.value !== null &&
										sieveLifeValue.value !== '0' ? (
											<span className={s.labelValues}>
												{` (${
													typeof sieveLifeValue.value === 'string'
														? Number(sieveLifeValue.value) / 10
														: sieveLifeValue.value / 10
												}%)`}
											</span>
										) : (
											''
										)}
									</div>
									<span className={s.fieldIcon}>
										{sieveLifeValue?.is_failed === true ? (
											<AlertIcon />
										) : sieveLifeValue?.is_actual === true ? (
											<StatusIcon />
										) : (
											''
										)}
									</span>
								</>
							) : (
								<div className={s.labelValues}>-</div>
							)}
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>{i18next.t('oxygen_purity')}</span>
							{this.props.loadingHealthCheckData === true ? (
								<div className={s.labelValues}>
									<Spinner small />
								</div>
							) : oxygenPurityValue.value !== null &&
							  oxygenPurityValue.value !== undefined ? (
								<>
									<div className={s.labelValues}>
										{oxygenPurityValue?.is_failed === true
											? i18next.t('bad')
											: oxygenPurityValue?.is_actual === true
											  ? i18next.t('good')
											  : '-'}

										{oxygenPurityValue.value !== null &&
										oxygenPurityValue.value !== '0' ? (
											<span className={s.labelValues}>
												{` (${
													typeof oxygenPurityValue.value === 'string'
														? Number(oxygenPurityValue.value) / 10
														: oxygenPurityValue.value / 10
												}%)`}
											</span>
										) : (
											''
										)}
									</div>
									<span className={s.fieldIcon}>
										{oxygenPurityValue?.is_failed === true ? (
											<AlertIcon />
										) : oxygenPurityValue?.is_actual === true ? (
											<StatusIcon />
										) : (
											''
										)}
									</span>
								</>
							) : (
								<div className={s.labelValues}>-</div>
							)}
						</div>
					</div>

					<div className={s.fieldWrapper}>
						<div className={s.fieldFlex}>
							<span className={s.labelField}>{i18next.t('life_clock')}</span>
							{this.props.loadingHealthCheckData === true ? (
								<div className={s.labelValues}>
									<Spinner small />
								</div>
							) : lifeClockValue?.value ? (
								<div className={s.labelValues}>{result}</div>
							) : (
								<div className={s.labelValues}>-</div>
							)}
						</div>
					</div>
				</>
			);
		} else {
			fieldsToRender = null;
		}

		return (
			<div style={dataListStyle}>
				{title && (
					<div className={s.modalTitle}>
						{titleIcon} {title}
					</div>
				)}
				{fieldsToRender}
				<ModalPopUp
					show={this.state.showMap}
					modalClosed={this.closeMap}
					modalTitle={i18next.t('last_location')}
				>
					{(() => {
						const loc = this.props?.dataList?.[0]?.last_location || {};
						const formatTimestamp = (ts) =>
							ts
								? new Date(ts).toLocaleString(undefined, {
										year: 'numeric',
										month: 'short',
										day: 'numeric',
										hour: '2-digit',
										minute: '2-digit',
								  })
								: '-';
						const coords =
							typeof loc?.latitude === 'number' &&
							typeof loc?.longitude === 'number'
								? `${loc.latitude}, ${loc.longitude}`
								: '-';

						return (
							<div>
								<div style={{ fontSize: 14, color: '#333', marginBottom: 12 }}>
									<div style={{ marginBottom: 4 }}>
										<span style={{ fontWeight: 600 }}>
											{i18next.t('location_label')}
										</span>{' '}
										<span>{loc?.last_location || '-'}</span>
									</div>
									<div style={{ marginBottom: 4 }}>
										<span style={{ fontWeight: 600 }}>
											{i18next.t('coordinates_label')}
										</span>{' '}
										<span>{coords}</span>
									</div>
									{(() => {
										const ip = loc?.updated_ip_at
											? new Date(loc.updated_ip_at)
											: null;
										const gps = loc?.updated_gps_at
											? new Date(loc.updated_gps_at)
											: null;
										let latest = null;
										if (ip && gps) {
											latest = ip > gps ? ip : gps;
										} else if (gps) {
											latest = gps;
										} else if (ip) {
											latest = ip;
										}
										if (latest) {
											return (
												<div style={{ marginBottom: 2 }}>
													<span style={{ fontWeight: 600 }}>
														{i18next.t('last_updated_at_label')}
													</span>{' '}
													<span>{formatTimestamp(latest)}</span>
												</div>
											);
										}
										return null;
									})()}
								</div>
								{typeof loc?.latitude === 'number' &&
								typeof loc?.longitude === 'number' ? (
									<div
										style={{ width: '100%', height: 400 }}
										ref={(node) => (this.mapNode = node)}
									/>
								) : (
									<div style={{ padding: '12px 0', color: '#666' }}>
										{i18next.t('map_unavailable_message')}
									</div>
								)}
							</div>
						);
					})()}
				</ModalPopUp>
			</div>
		);
	}
}
DataList.propTypes = {
	dataList: PropTypes.array,
	title: PropTypes.string,
	notificationButton: PropTypes.node,
	isAllowUpdate: PropTypes.bool,
};

export default DataList;
